import { z } from 'zod';

import { Id, IsoDateTime, Paise } from '../common/primitives';

export { Paise } from '../common/primitives';

// ── Enums ───────────────────────────────────────────────────────────────────

export const SettlementScheduleSchema = z.enum(['T_PLUS_1', 'T_PLUS_7', 'T_PLUS_14', 'MANUAL']);
export type SettlementSchedule = z.infer<typeof SettlementScheduleSchema>;

export const BankAccountStatusSchema = z.enum(['PENDING', 'VERIFIED', 'REJECTED', 'DISABLED']);
export type BankAccountStatus = z.infer<typeof BankAccountStatusSchema>;

export const SettlementStatusSchema = z.enum([
  'PENDING_COD_REMITTANCE',
  'PENDING_REFUND_WINDOW',
  'ELIGIBLE',
  'SETTLED',
  'HELD',
  'REVERSED',
]);
export type SettlementStatus = z.infer<typeof SettlementStatusSchema>;

export const PayoutStatusSchema = z.enum(['PENDING', 'APPROVED', 'RELEASED', 'REJECTED', 'FAILED']);
export type PayoutStatus = z.infer<typeof PayoutStatusSchema>;

// ── Response shapes ─────────────────────────────────────────────────────────

export const VendorBankAccountSchema = z.object({
  id: Id,
  vendorId: Id,
  accountHolderName: z.string(),
  /// Masked for client-side display — never returns the full account number.
  accountNumberMasked: z.string(),
  ifsc: z.string(),
  isDefault: z.boolean(),
  status: BankAccountStatusSchema,
  verifiedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type VendorBankAccount = z.infer<typeof VendorBankAccountSchema>;

/// Admin-only view: the full number is needed to verify the account and to pay into it by hand.
export const AdminVendorBankAccountSchema = VendorBankAccountSchema.extend({
  accountNumber: z.string(),
});
export type AdminVendorBankAccount = z.infer<typeof AdminVendorBankAccountSchema>;

export const SettlementSchema = z.object({
  id: Id,
  subOrderId: Id,
  vendorId: Id,
  status: SettlementStatusSchema,
  grossPaise: Paise,
  commissionPaise: Paise,
  /** Terra Shoppy's share of offer discounts, paid to the seller on top of the sale. */
  platformSupportPaise: Paise,
  tcsPaise: Paise,
  tdsPaise: Paise,
  /** Courier freight charged to the seller. */
  shippingFeePaise: Paise,
  reserveHeldPaise: Paise,
  netPayablePaise: Paise,
  reserveReleaseAt: IsoDateTime.nullable(),
  eligibleAt: IsoDateTime.nullable(),
  settledAt: IsoDateTime.nullable(),
  ledgerTxId: Id.nullable(),
  notes: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type Settlement = z.infer<typeof SettlementSchema>;

export const PayoutRequestSchema = z.object({
  id: Id,
  vendorId: Id,
  bankAccountId: Id.nullable(),
  runId: Id.nullable(),
  amountPaise: Paise,
  status: PayoutStatusSchema,
  provider: z.string().nullable(),
  providerPayoutId: z.string().nullable(),
  providerStatus: z.string().nullable(),
  failureReason: z.string().nullable(),
  bankRef: z.string().nullable(),
  note: z.string().nullable(),
  requestedAt: IsoDateTime,
  approvedAt: IsoDateTime.nullable(),
  releasedAt: IsoDateTime.nullable(),
});
export type PayoutRequest = z.infer<typeof PayoutRequestSchema>;

export const SettlementRunSummarySchema = z.object({
  id: Id,
  runDate: IsoDateTime,
  status: z.string(),
  totals: z
    .object({
      vendorCount: z.number().int().nonnegative(),
      subOrderCount: z.number().int().nonnegative(),
      grossPaise: Paise,
      commissionPaise: Paise,
      tcsPaise: Paise,
      tdsPaise: Paise,
      reservePaise: Paise,
      netPaise: Paise,
      payoutCount: z.number().int().nonnegative(),
    })
    .nullable(),
  startedAt: IsoDateTime,
  completedAt: IsoDateTime.nullable(),
});
export type SettlementRunSummary = z.infer<typeof SettlementRunSummarySchema>;

export const VendorPayoutEligibilitySchema = z.object({
  vendorId: Id,
  receivablePaise: Paise,
  heldPaise: Paise,
  withdrawablePaise: Paise,
  payoutThresholdPaise: Paise,
  eligibleForAutoPayout: z.boolean(),
});
export type VendorPayoutEligibility = z.infer<typeof VendorPayoutEligibilitySchema>;

// ── Request shapes ──────────────────────────────────────────────────────────

export const UpdateVendorSettlementConfigRequest = z
  .object({
    settlementSchedule: SettlementScheduleSchema.optional(),
    rollingReserveBps: z.number().int().min(0).max(2000).optional(),
    rollingReserveDays: z.number().int().min(0).max(180).optional(),
    payoutThresholdPaise: Paise.optional(),
    panNumber: z
      .string()
      .trim()
      .regex(/^[A-Z]{5}[0-9]{4}[A-Z]$/, 'Invalid PAN format')
      .optional(),
    payoutFrozen: z.boolean().optional(),
  })
  .strict();
export type UpdateVendorSettlementConfigRequest = z.infer<
  typeof UpdateVendorSettlementConfigRequest
>;

export const AddBankAccountRequest = z
  .object({
    accountHolderName: z.string().trim().min(2).max(120),
    accountNumber: z
      .string()
      .trim()
      .regex(/^\d{9,18}$/, 'Account number must be 9–18 digits'),
    ifsc: z
      .string()
      .trim()
      .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC format'),
    isDefault: z.boolean().optional(),
  })
  .strict();
export type AddBankAccountRequest = z.infer<typeof AddBankAccountRequest>;

export const RequestPayoutRequest = z
  .object({
    amountPaise: Paise.refine((v) => v > 0, { message: 'Must be positive' }),
    bankAccountId: Id.optional(),
    note: z.string().trim().max(500).optional(),
  })
  .strict();
export type RequestPayoutRequest = z.infer<typeof RequestPayoutRequest>;

export const ApprovePayoutRequest = z
  .object({
    note: z.string().trim().max(500).optional(),
  })
  .strict();
export type ApprovePayoutRequest = z.infer<typeof ApprovePayoutRequest>;

export const RejectPayoutRequest = z
  .object({
    reason: z.string().trim().min(4).max(500),
  })
  .strict();
export type RejectPayoutRequest = z.infer<typeof RejectPayoutRequest>;

export const ReleasePayoutRequest = z
  .object({
    /// Manual external transfer reference (UTR / cheque no) when not using a programmatic payout.
    bankRef: z.string().trim().max(100).optional(),
    /// Programmatic provider name; if set, the platform expects the provider to dispatch.
    provider: z.enum(['RAZORPAY_X', 'MANUAL']).default('MANUAL'),
  })
  .strict();
export type ReleasePayoutRequest = z.infer<typeof ReleasePayoutRequest>;

// ── Admin finance overview ─────────────────────────────────────────────────
//
// Every rupee a customer pays sits in exactly one of these buckets until it
// leaves as a payout. This is the same pipeline Amazon, Flipkart and Meesho
// run — money is held on delivery, released after the return window, and paid
// out on a cycle — and the point of the overview is to show a platform owner
// where the money currently *is*, without opening a vendor at a time.
//
// The buckets are computed, never stored: each one is a sum over rows that
// already exist (payments, sub-orders, settlements, payout requests), so the
// overview can never disagree with the pages that list those rows.

/** One row of the pipeline, in paise, with the count of orders behind it. */
export const FinanceBucketSchema = z.object({
  paise: Paise,
  count: z.number().int().nonnegative(),
});
export type FinanceBucket = z.infer<typeof FinanceBucketSchema>;

export const AdminFinanceOverviewSchema = z.object({
  /** Captured online payments plus collected COD, for the period. What customers actually paid. */
  received: FinanceBucketSchema,
  /** Delivered, but the customer may still return it. Nothing here is the vendor's yet. */
  inReturnWindow: FinanceBucketSchema,
  /** COD delivered, courier has not yet handed the cash over. */
  awaitingCodRemittance: FinanceBucketSchema,
  /** Return window closed, waiting for the next settlement run. */
  eligible: FinanceBucketSchema,
  /** Settled into vendor balances this period — after commission, TCS, TDS and reserve. */
  settledNet: FinanceBucketSchema,
  /** What the platform kept from those settlements. */
  platformCommission: FinanceBucketSchema,
  /** Payout requests waiting for an admin decision. */
  payoutsPending: FinanceBucketSchema,
  /** Approved but not yet transferred. */
  payoutsApproved: FinanceBucketSchema,
  /** Actually transferred to sellers' banks this period. */
  payoutsReleased: FinanceBucketSchema,
  /** Refunds issued this period — money that went back to customers. */
  refunded: FinanceBucketSchema,
  /** When the last settlement run finished, or null if none has ever run. Null here is a fault, not a quiet day. */
  lastRunAt: IsoDateTime.nullable(),
  from: IsoDateTime,
  to: IsoDateTime,
});
export type AdminFinanceOverview = z.infer<typeof AdminFinanceOverviewSchema>;

export const AdminFinanceOverviewQuery = z
  .object({
    /** ISO date. Defaults to 30 days ago. */
    from: z.string().datetime().optional(),
    /** ISO date. Defaults to now. */
    to: z.string().datetime().optional(),
  })
  .strict();
export type AdminFinanceOverviewQuery = z.infer<typeof AdminFinanceOverviewQuery>;

/**
 * Where one sub-order's money is, for a row in the admin orders table.
 *
 * Derived from the sub-order and its settlement row, so an order the run has
 * not looked at yet still gets an honest answer ("delivered, window open
 * until …") instead of a blank.
 */
export const OrderSettlementStageSchema = z.enum([
  'NOT_DELIVERED', // nothing to settle yet
  'AWAITING_COD_REMITTANCE',
  'IN_RETURN_WINDOW',
  'ELIGIBLE',
  'SETTLED',
  'PAID_OUT',
  'HELD',
  'REVERSED',
  'CANCELLED',
]);
export type OrderSettlementStage = z.infer<typeof OrderSettlementStageSchema>;

export const OrderSettlementStatusSchema = z.object({
  subOrderId: Id,
  stage: OrderSettlementStageSchema,
  /** When the return window closes; only set while IN_RETURN_WINDOW. */
  eligibleAt: IsoDateTime.nullable(),
  /** Net the vendor gets (or got); null until settled. */
  netPayablePaise: Paise.nullable(),
  settledAt: IsoDateTime.nullable(),
});
export type OrderSettlementStatus = z.infer<typeof OrderSettlementStatusSchema>;

// ── Seller payouts (admin, cross-vendor) ────────────────────────────────────

/**
 * How a page of seller payout rows is ordered.
 *
 * A closed set, because the value reaches an `orderBy` clause. `sellerName` is
 * deliberately absent: the rows are produced by a grouped aggregate over
 * `Settlement`, and a grouped query cannot order by a column on the joined
 * vendor. Sorting the page in memory would order each page against itself and
 * silently lie about the rest of the result, so the option is not offered
 * rather than offered and wrong. Searching by name is unaffected.
 */
export const SellerPayoutSortSchema = z.enum([
  'netPayable',
  'gross',
  'commission',
  'reserve',
  'orders',
  'lastSettledAt',
]);
export type SellerPayoutSort = z.infer<typeof SellerPayoutSortSchema>;

export const SellerPayoutsQuery = z
  .object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(25),
    /** Matches the store name, its slug, or the seller's e-mail. */
    q: z.string().trim().min(1).max(120).optional(),
    /**
     * Narrows to one stage of the money. These are `SettlementStatus` values —
     * the state the money is actually in — not payout-request states, which
     * live on the separate `latestPayout` field of each row.
     */
    status: SettlementStatusSchema.optional(),
    sortBy: SellerPayoutSortSchema.default('netPayable'),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
  })
  .strict();
export type SellerPayoutsQuery = z.infer<typeof SellerPayoutsQuery>;

/**
 * The seller's most recent payout request, if they have ever made one.
 *
 * Reported beside the settlement totals rather than merged into them.
 * `PayoutRequest` and `Settlement` share no foreign key — both only carry a
 * `vendorId`, and a payout's optional `runId` points at a settlement *run*,
 * not at the rows in this aggregate. So this says "this seller's latest payout
 * request", which is true, and never "this payout covers this money", which
 * would not be.
 */
export const SellerLatestPayoutSchema = z.object({
  id: Id,
  status: PayoutStatusSchema,
  amountPaise: Paise,
  requestedAt: IsoDateTime,
  approvedAt: IsoDateTime.nullable(),
  releasedAt: IsoDateTime.nullable(),
});
export type SellerLatestPayout = z.infer<typeof SellerLatestPayoutSchema>;

/** One seller's settlement money, aggregated — one row of the admin payouts table. */
export const SellerPayoutRowSchema = z.object({
  vendorId: Id,
  storeName: z.string(),
  slug: z.string(),
  /** The seller's account e-mail. Null when the vendor has no linked user. */
  email: z.string().nullable(),
  /** Payouts are blocked for this seller until an admin lifts it. */
  payoutFrozen: z.boolean(),

  /** Settlements in scope. Each settlement is exactly one sub-order. */
  ordersCount: z.number().int().nonnegative(),
  grossPaise: Paise,
  commissionPaise: Paise,
  tcsPaise: Paise,
  tdsPaise: Paise,
  shippingFeePaise: Paise,
  reserveHeldPaise: Paise,
  netPayablePaise: Paise,

  /**
   * The stage this seller's money is mostly in, for the row's status chip.
   * Picked as the least-advanced stage present, because a seller with one
   * order still held and nine settled is blocked, not settled.
   */
  status: SettlementStatusSchema,
  /** Every stage present for this seller, so a row can be explained without another call. */
  statusBreakdown: z.array(
    z.object({
      status: SettlementStatusSchema,
      count: z.number().int().nonnegative(),
      netPayablePaise: Paise,
    }),
  ),
  /** Most recent `settledAt` across the aggregate; null while nothing has settled. */
  lastSettledAt: IsoDateTime.nullable(),
  latestPayout: SellerLatestPayoutSchema.nullable(),
});
export type SellerPayoutRow = z.infer<typeof SellerPayoutRowSchema>;

export const SellerPayoutsResponseSchema = z.object({
  items: z.array(SellerPayoutRowSchema),
  pagination: z.object({
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
  }),
});
export type SellerPayoutsResponse = z.infer<typeof SellerPayoutsResponseSchema>;
